<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CalendarMonthView from 'dashboard/components-next/Calendar/CalendarMonthView.vue';
import CalendarWeekView from 'dashboard/components-next/Calendar/CalendarWeekView.vue';
import CalendarDayView from 'dashboard/components-next/Calendar/CalendarDayView.vue';
import CalendarAgendaView from 'dashboard/components-next/Calendar/CalendarAgendaView.vue';
import FollowUpEditDialog from 'dashboard/components-next/FollowUp/FollowUpEditDialog.vue';
import { useFollowUp } from 'dashboard/components-next/FollowUp/composables/useFollowUp.js';
import { useCalendar } from 'dashboard/components-next/Calendar/composables/useCalendar.js';

const { t, locale } = useI18n();
const { completeFollowUp } = useFollowUp();

const {
  viewMode,
  currentDate,
  monthGridWeeks,
  visibleRange,
  events,
  isLoading,
  setViewMode,
  goToday,
  goNext,
  goPrev,
  fetchEvents,
  eventsForDay,
} = useCalendar();

const VIEW_MODES = [
  { key: 'month', labelKey: 'CALENDAR.VIEW_MODES.MONTH' },
  { key: 'week', labelKey: 'CALENDAR.VIEW_MODES.WEEK' },
  { key: 'day', labelKey: 'CALENDAR.VIEW_MODES.DAY' },
  { key: 'agenda', labelKey: 'CALENDAR.VIEW_MODES.AGENDA' },
];

const headerLabel = computed(() => {
  if (viewMode.value === 'day') {
    return currentDate.value.toLocaleDateString(locale.value, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  if (viewMode.value === 'agenda') {
    return t('CALENDAR.VIEW_MODES.AGENDA');
  }
  return currentDate.value.toLocaleDateString(locale.value, {
    month: 'long',
    year: 'numeric',
  });
});

onMounted(fetchEvents);

// One shared edit dialog for whichever event's "Reschedule" was clicked —
// same component the Contact page and Conversation sidebar already use,
// so rescheduling from the Calendar goes through the identical Follow-up
// form instead of a bespoke Calendar-only editor.
const rescheduleDialogRef = ref(null);
const reschedulingContact = ref(null);

const handleReschedule = async event => {
  reschedulingContact.value = event.contact;
  await nextTick();
  rescheduleDialogRef.value?.dialogRef?.open();
};

const handleDialogSaved = () => {
  rescheduleDialogRef.value?.dialogRef?.close();
  fetchEvents();
};

const handleComplete = async event => {
  const completed = await completeFollowUp(event.contact.id);
  if (completed) fetchEvents();
};
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 px-4 py-3">
    <div
      class="flex flex-wrap items-center justify-between flex-shrink-0 gap-2 px-1 pb-3"
    >
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('CALENDAR.TITLE') }}
      </h1>
      <div class="flex items-center gap-2">
        <div class="flex items-center border rounded-lg border-n-weak">
          <button
            v-for="mode in VIEW_MODES"
            :key="mode.key"
            type="button"
            class="px-3 py-1.5 text-sm font-medium first:rounded-l-lg last:rounded-r-lg"
            :class="
              viewMode === mode.key
                ? 'bg-n-brand text-white'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="setViewMode(mode.key)"
          >
            {{ t(mode.labelKey) }}
          </button>
        </div>
        <Button
          icon="i-lucide-chevron-left"
          variant="faded"
          color="slate"
          size="sm"
          @click="goPrev"
        />
        <Button
          :label="t('CALENDAR.TODAY')"
          variant="faded"
          color="slate"
          size="sm"
          @click="goToday"
        />
        <Button
          icon="i-lucide-chevron-right"
          variant="faded"
          color="slate"
          size="sm"
          @click="goNext"
        />
      </div>
    </div>

    <div class="flex-shrink-0 px-1 pb-2 text-sm font-medium text-n-slate-11">
      {{ headerLabel }}
    </div>

    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <Spinner />
    </div>
    <template v-else>
      <CalendarMonthView
        v-if="viewMode === 'month'"
        :weeks="monthGridWeeks"
        :current-date="currentDate"
        :events-for-day="eventsForDay"
        class="flex-1 min-h-0"
        @reschedule="handleReschedule"
        @complete="handleComplete"
      />
      <CalendarWeekView
        v-else-if="viewMode === 'week'"
        :visible-range="visibleRange"
        :events-for-day="eventsForDay"
        class="flex-1 min-h-0"
        @reschedule="handleReschedule"
        @complete="handleComplete"
      />
      <CalendarDayView
        v-else-if="viewMode === 'day'"
        :current-date="currentDate"
        :events-for-day="eventsForDay"
        class="flex-1 min-h-0"
        @reschedule="handleReschedule"
        @complete="handleComplete"
      />
      <CalendarAgendaView
        v-else
        :events="events"
        class="flex-1 min-h-0"
        @reschedule="handleReschedule"
        @complete="handleComplete"
      />
    </template>

    <FollowUpEditDialog
      v-if="reschedulingContact"
      ref="rescheduleDialogRef"
      :contact-id="reschedulingContact.id"
      :has-conversation="!!reschedulingContact.last_conversation_at"
      :email="reschedulingContact.email"
      :next-follow-up-at="
        reschedulingContact.custom_attributes?.next_follow_up_at
      "
      :follow-up-note="reschedulingContact.custom_attributes?.follow_up_note"
      :meeting-required="
        reschedulingContact.custom_attributes?.meeting_required
      "
      :meeting-provider="
        reschedulingContact.custom_attributes?.meeting_provider
      "
      :meeting-url="reschedulingContact.custom_attributes?.meeting_url"
      :meeting-reference="
        reschedulingContact.custom_attributes?.meeting_reference
      "
      :follow-up-type="reschedulingContact.custom_attributes?.follow_up_type"
      :meeting-location="
        reschedulingContact.custom_attributes?.meeting_location
      "
      :follow-up-team-id="
        reschedulingContact.custom_attributes?.follow_up_team_id
      "
      :assignee="reschedulingContact.assignee"
      @saved="handleDialogSaved"
    />

    <div
      class="flex items-center flex-shrink-0 gap-4 px-1 pt-3 text-xs text-n-slate-10"
    >
      <span class="flex items-center gap-1.5">
        <span class="rounded-full size-2 bg-n-ruby-9" />
        {{ t('CALENDAR.LEGEND.OVERDUE') }}
      </span>
      <span class="flex items-center gap-1.5">
        <span class="rounded-full size-2 bg-n-amber-9" />
        {{ t('CALENDAR.LEGEND.DUE_TODAY') }}
      </span>
      <span class="flex items-center gap-1.5">
        <span class="rounded-full size-2 bg-n-slate-9" />
        {{ t('CALENDAR.LEGEND.UPCOMING') }}
      </span>
      <span class="flex items-center gap-1.5">
        <span class="rounded-full size-2 bg-n-iris-9" />
        {{ t('CALENDAR.LEGEND.MEETING_SCHEDULED') }}
      </span>
    </div>
  </div>
</template>
